import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PROJECT_KEYS } from "./keys";
import { PaginationParams } from "../type";
import { projectServices } from "./project.services";
import { UpdateProjectParams } from "./project.types";

export const useGetProjects = (params?: PaginationParams) => {
  return useQuery({
    queryKey: PROJECT_KEYS.GET_PROJECTS(params),
    queryFn: () => projectServices.getProjects(params),
  });
};

export const useGetProjectsInfinite = (params?: PaginationParams) => {
  return useInfiniteQuery({
    queryKey: PROJECT_KEYS.GET_PROJECTS(params),
    queryFn: ({ pageParam }) =>
      projectServices.getProjects({ ...params, page: pageParam }),
    getNextPageParam: (lastPage) => {
      const { currentPage, totalPages } = lastPage.meta;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    initialPageParam: 1,
  });
};

export const useGetProjectsByInventoryServiceTickets = (clientId?: string) => {
  return useQuery({
    queryKey: [...PROJECT_KEYS.GET_PROJECTS({}), 'byInventoryServiceTickets', clientId],
    queryFn: () => projectServices.getProjectsByInventoryServiceTickets(clientId),
    enabled: !!clientId,
  });
};

export const useGetProject = (id: string) => {
  return useQuery({
    queryKey: [...PROJECT_KEYS.GET_PROJECTS({}), id],
    queryFn: () => projectServices.getProject(id),
    enabled: !!id,
  });
};

export const useUpdateProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProjectParams }) =>
      projectServices.updateProject(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROJECT_KEYS.GET_PROJECTS({}) });
    },
  });
};

export const useDeleteProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => projectServices.deleteProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROJECT_KEYS.GET_PROJECTS({}) });
    },
  });
};
